/**
 * Identidade da barbearia, aplicada em runtime.
 *
 * ---
 *
 * O QUE MUDOU: A COR DEIXOU DE SER DA BARBEARIA
 *
 * Até a Etapa 14 este módulo também escrevia `--brand-primary` e companhia a
 * partir de `/public/tenant`, e cada barbearia pintava o app com a própria cor.
 * A decisão passou a ser outra: os três frontends usam a identidade visual da
 * plataforma (o design system StackPlus, em
 * `@barber/ui/theme/stackplus.css`), e a cor cadastrada pela barbearia não
 * pinta mais nada.
 *
 * A troca não foi só de paleta — foi de mecanismo. Com cor por tenant, a
 * escolha do texto sobre a marca precisava ser CALCULADA a partir da luminância
 * (uma barbearia que escolhesse amarelo-ouro e recebesse branco fixo publicaria
 * um botão ilegível). Com paleta fixa, o par cor/texto é decidido uma vez, no
 * CSS, e a matemática de contraste deixou de ter consumidor. Ela saiu junto:
 * ficaria como código que ninguém executa e que a próxima pessoa leria como
 * ainda no ar. Está no histórico do git, inteira e testada, se voltar a ser
 * necessária.
 *
 * ---
 *
 * O QUE CONTINUA POR BARBEARIA
 *
 * Nome, logo, favicon e o nome do atalho na tela inicial. É o que aparece na
 * aba do navegador e na tela do celular de quem instalou o PWA — e é o que
 * responde "de quem é este app", que a cor nunca respondeu sozinha.
 */

export interface TenantBranding {
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly faviconUrl?: string | null;
}

/** Near-black e dourado do StackPlus, iguais em todos os apps. */
export const PLATFORM_THEME = {
  background: '#1C1C1C',
  accent: '#EFA247',
} as const;

/**
 * Nome e ícone do app.
 *
 * O manifest do build é o genérico da plataforma; aqui ele é substituído por um
 * gerado em memória, com o nome desta barbearia. É o que faz o atalho na tela
 * inicial dizer "Barbearia Alfa" e não "Agendamento" — sem um build por
 * barbearia.
 */
export function applyTenantIdentity(input: {
  name: string;
  branding?: Partial<TenantBranding> | null;
  description?: string | null;
}): void {
  document.title = input.name;

  const favicon = input.branding?.faviconUrl;

  if (favicon) {
    const link =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
      document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }));

    link.href = favicon;
    // O type do SVG padrão não vale para um PNG do tenant.
    link.removeAttribute('type');
  }

  const manifest = {
    name: input.name,
    // O `trim` importa: "Studio Beta Barber" cortado em 12 vira "Studio Beta "
    // com espaço no fim, e é esse texto que o sistema operacional escreve sob o
    // ícone na tela inicial.
    short_name: input.name.slice(0, 12).trim(),
    description: input.description ?? `Agende seu horário na ${input.name}`,
    start_url: '/',
    display: 'standalone',
    orientation: 'portrait',
    // As cores do manifest são as da PLATAFORMA, e precisam bater com o
    // `theme-color` do index.html: divergentes, a tela de abertura do PWA pisca
    // uma cor e o app abre em outra.
    background_color: PLATFORM_THEME.background,
    theme_color: PLATFORM_THEME.background,
    icons: [
      favicon
        ? { src: favicon, sizes: 'any' }
        : { src: '/icon.svg', sizes: 'any', type: 'image/svg+xml' },
    ],
  };

  const url = URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: 'application/json' }),
  );

  const link =
    document.querySelector<HTMLLinkElement>('link[rel="manifest"]') ??
    document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'manifest' }));

  // O anterior era um blob nosso: soltá-lo evita vazar memória a cada troca.
  if (link.href.startsWith('blob:')) URL.revokeObjectURL(link.href);

  link.href = url;
}
